import { useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  CriteriaModel,
  ActionModel,
  FilterModel,
} from '../../../../../../types';
import { useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  betaGetGmailLabels,
  getGmailFilters,
  getUserQuery,
} from '../../../../../api/users-api';
import DeleteFiltersDialog from './delete-gmail-filters.dialog';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import AddGmailFilterDialog from './add-gmail-filter-dialog';
import GmailFiltersToolbar from './gmail-filters-toolbar';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import { Messages } from '../../../../../utils/message-constants';
import { createGridOverlays } from '../../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../../../shared/shared-data-grid-toolbar-helper';

import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Mail from '@mui/icons-material/Mail';

export default function GmailFiltersGrid() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { data: user } = useQuery(getUserQuery(userEmail));

  const [openAddFilter, setOpenAddFilter] = useState(false);
  const [openDeleteFilters, setOpenDeleteFilters] = useState(false);

  const {
    data: gmailFilters,
    isLoading: gmailFiltersInitialLoading,
    isFetching: gmailFiltersFetching,
  } = useQuery({
    queryKey: [UserKeys.GmailFilters, userEmail],
    queryFn: () => getGmailFilters(userEmail),
    enabled: isAbleToSeeUIElement(userRoles, UserRoles.ViewGmailFilters.value),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('gmail filters'),
    },
  });

  const { data: gmailLabels } = useQuery({
    queryKey: [UserKeys.GmailLabels, userEmail],
    queryFn: () => betaGetGmailLabels(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('gmail labels'),
    },
  });

  const sortedGmailLabels =
    gmailLabels
      ?.filter((label) => label.type === 'user')
      .sort((a, b) => a.name.localeCompare(b.name)) || [];

  const columns: GridColDef<FilterModel>[] = [
    {
      field: 'criteria',
      valueGetter: (value: CriteriaModel | null | undefined) => {
        if (!value) {
          return '';
        }

        let returnValue = '';

        if (value.from != null) {
          returnValue += `from:(${value.from}) `;
        }

        if (value.to != null) {
          returnValue += `to:(${value.to}) `;
        }

        if (value.subject != null) {
          returnValue += `subject:${value.subject} `;
        }

        if (value.query != null) {
          returnValue += `${value.query} `;
        }

        if (value.negatedQuery != null) {
          returnValue += `-{${value.negatedQuery}} `;
        }

        if (value.hasAttachment) {
          returnValue += `has:attachment `;
        }

        if (value.sizeComparison != null) {
          let displaySize: string | number = value.size;
          if (value.size % 1000000 === 0) {
            displaySize = `${value.size / 1000000}M`;
          } else if (value.size % 1000 === 0) {
            displaySize = `${value.size / 1000}K`;
          }
          returnValue += `${value.sizeComparison}:${displaySize}`;
        }

        return returnValue.replace(/,\s*$/, '');
      },
      headerName: 'Matches',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'action',
      valueGetter: (value: ActionModel | null) => {
        let returnValue = '';

        value?.removeLabelIds.forEach((labelId) => {
          if (labelId === 'INBOX') {
            returnValue += 'Skip Inbox, ';
          }
          if (labelId === 'UNREAD') {
            returnValue += 'Mark as read, ';
          }
          if (labelId === 'IMPORTANT') {
            returnValue += 'Never mark it as important, ';
          }
          if (labelId === 'SPAM') {
            returnValue += 'Never send it to Spam, ';
          }
        });

        value?.addLabelIds.forEach((labelId) => {
          if (labelId === 'STARRED') {
            returnValue += 'Star it, ';
          } else if (labelId === 'TRASH') {
            returnValue += 'Delete it, ';
          } else if (labelId === 'IMPORTANT') {
            returnValue += 'Mark it as important, ';
          } else if (labelId === 'CATEGORY_PERSONAL') {
            returnValue += 'Categorize as Primary, ';
          } else if (labelId === 'CATEGORY_SOCIAL') {
            returnValue += 'Categorize as Social, ';
          } else if (labelId === 'CATEGORY_PROMOTIONS') {
            returnValue += 'Categorize as Promotions, ';
          } else if (labelId === 'CATEGORY_UPDATES') {
            returnValue += 'Categorize as Updates, ';
          } else if (labelId === 'CATEGORY_FORUMS') {
            returnValue += 'Categorize as Forums, ';
          } else {
            const labelName = gmailLabels?.find((label) => label.id === labelId)
              ?.name;

            returnValue += 'Apply label "' + labelName + '", ';
          }
        });

        if (value?.forward) {
          returnValue += 'Forward to ' + value.forward;
        }

        return returnValue.replace(/,\s*$/, '');
      },
      headerName: 'Do This',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      display: 'flex',
    },
  ];

  const toolbarProps = useMemo(
    () => ({
      userRoles,
      userEmail,
      selectedRows,
      setOpenAddGmailFilter: setOpenAddFilter,
      setOpenDeleteGmailFilters: setOpenDeleteFilters,
    }),
    [selectedRows, userEmail, userRoles]
  );

  const CustomToolbar = useToolbar(GmailFiltersToolbar, toolbarProps);

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Gmail Filters', <Mail />),
        }}
        rows={gmailFilters || []}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        toolbarQuickFilterWidth={400}
        loading={gmailFiltersInitialLoading || gmailFiltersFetching}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        disableColumnSelector
        checkboxSelection
        disableColumnMenu
      />
      {openAddFilter && (
        <AddGmailFilterDialog
          open={openAddFilter}
          onClose={() => setOpenAddFilter(false)}
          userEmail={userEmail}
          userRoles={userRoles}
          changeLogRoles={changeLogRoles}
          sortedGmailLabels={sortedGmailLabels}
        />
      )}
      {selectedRows.length > 0 && gmailFilters ? (
        <DeleteFiltersDialog
          open={openDeleteFilters}
          handleClose={() => setOpenDeleteFilters(false)}
          selectedRows={selectedRows}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
